<template>
    <PricingBody
        v-if="pricing.uuid"
        :pricing="pricing"
        @updateIframeHeight="updateHeight"
        @changeLayout="changeLayout"
    />
</template>

<script>
import PricingBody from "@/views/tools/pricing/PricingBody.vue";
import { pricingsService } from "@/apps/pricing/services/pricing.service";
import { statsService } from "@/shared/statistics/stats.service";
import { setFontFamily, updateIframeHeight } from "@/shared/utils";
import { preparePricing } from "@/shared/projects/prepareData";
import { RENDER_ROUTE_NAMES } from "@/views/render/render.routes";
import { usePreviewLanguage } from "@/shared/utils/router/usePreviewLanguage";
import { projectsService } from "@/shared/projects/projects.service";
import { makeLogger } from "@/shared/consola";
import { isCustomDomain } from "@/shared/connection/service.helpers";

const EASY_CART_HOSTS = ["cart.easy.tools", "app.easycart.pl", "easycart.test", "dev.easycart.pl"];

const logger = makeLogger("PricingPage");

export default {
    name: "PricingPage",
    components: {
        PricingBody,
    },
    props: {
        isStorefront: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        isPricingView() {
            return this.$route.name === RENDER_ROUTE_NAMES.pricing;
        },
        isECTenantView() {
            return this.$route.name === RENDER_ROUTE_NAMES.pricingEcTenant;
        },
        isECProductView() {
            return this.$route.name === RENDER_ROUTE_NAMES.pricingEcProduct;
        },
    },
    data() {
        return {
            pricing: {},
            windowWidth: window.width,
            currentFont: "Inter",
            waiting: false,
        };
    },
    setup() {
        const { setLanguageIfNoQuery } = usePreviewLanguage();
        return { setLanguageIfNoQuery };
    },
    created() {
        if (this.isStorefront) {
            console.log('"storefront created!"');
            // document.querySelector("base")?.setAttribute("href", "/_et/");
        }
    },
    async mounted() {
        if (this.isStorefront) {
            return this.loadAsStorefront();
        }
        try {
            const { projectUuid, tenantSlug, productSlug } = this.$route.params;

            if (this.isPricingView) {
                this.pricing = await this.loadPricing(projectUuid);
            } else {
                const isEasyCart = this.checkIsEasyCart();
                this.pricing = await this.loadECPricing(tenantSlug, productSlug, isEasyCart);
            }

            //in case there is no lang in query chose project ownwer's language
            this.setLanguageIfNoQuery(this.pricing?.user?.language);

            this.adjustIframe();

            window.addEventListener("resize", this.resizeIframe);

            await this.saveStat();
        } catch {
            /* eslint-disable-next-line no-empty */
        }
    },
    beforeUnmount() {
        window.removeEventListener("resize", this.resizeIframe);
    },
    methods: {
        async loadAsStorefront() {
            logger.debug("Welcome to storefront !");
            if (!isCustomDomain()) {
                logger.debug("Not a custom domain, redirecting to pricing page");
                return this.$router.push("/pricing/projects");
            }
            const { productSlug } = this.$route.params;
            try {
                const { tenantSlug } = await pricingsService.getTenantSlugFromDomain();
                logger.debug("tenantSlug for custom domain is: ", tenantSlug);
                if (!tenantSlug) {
                    logger.error("Tenant slug is not defined for custom domain");
                    return;
                }
                this.pricing = await this.loadECPricing(tenantSlug, productSlug);
                //in case there is no lang in query chose project ownwer's language
                this.setLanguageIfNoQuery(this.pricing?.user?.language);
                // await this.saveStat(); // should we record stats ??
            } catch (e) {
                logger.error("Error loading storefront pricing:", e);
            }
        },
        checkIsEasyCart() {
            if (window.location !== window.parent.location) {
                if (document.referrer) {
                    return EASY_CART_HOSTS.includes(new URL(document.referrer).host);
                }
                return false;
            }
            return false;
        },
        async loadPricing(uuid) {
            this.waiting = true;

            const { pricing, ecPricing, ecVariants } = await projectsService.getOneForRender({
                tool: "pricing",
                uuid,
            });

            pricing.ecPricing = ecPricing;
            pricing.ecVariants = ecVariants;

            this.waiting = false;
            return preparePricing(pricing);
        },
        async loadECPricing(tenantSlug, productSlug, isEasyCart = false) {
            const { pricing, ecPricing, ecVariants } = productSlug
                ? await pricingsService.getProductPricingForRender(tenantSlug, productSlug, {
                      isEasyCart,
                      isStorefront: this.isStorefront || undefined,
                  })
                : await pricingsService.getTenantPricingForRender(tenantSlug, {
                      isEasyCart,
                      isStorefront: this.isStorefront || undefined,
                  });

            pricing.ecPricing = ecPricing;
            pricing.ecVariants = ecVariants;

            this.waiting = false;
            return preparePricing(pricing);
        },
        async adjustIframe() {
            this.currentFont = setFontFamily(this.pricing.fontFamily, this.currentFont);
            await this.$nextTick();
            this.updateIframe();
        },
        updateIframe() {
            this.updateHeight();
        },
        updateHeight() {
            updateIframeHeight("pricing", this.pricing.uuid);
        },
        resizeIframe() {
            if (window.innerWidth !== this.windowWidth) {
                this.updateIframe();
                this.windowWidth = window.innerWidth;
            }
        },
        async changeLayout(layout) {
            this.pricing.layout = layout;
            await this.$nextTick();
            this.updateIframe();
        },
        async saveStat() {
            const { id, uuid } = this.pricing;
            await statsService.savePricingStats(id, uuid);
        },
    },
};
</script>
